import apiClient from '@/api/client';
import { getActiveHospitalId, getActiveClinicId } from '@/constants';
import {
  utcToKolkataParts,
  kolkataToUtcIso,
  formatDateTime,
  formatDate,
  formatTime,
  getKolkataStartOfDay,
  getKolkataEndOfDay,
} from '@/utils/dateTime';

/**
 * Transforms backend appointment entity (with UTC ISO timestamps)
 * into Asia/Kolkata localized date, time, and human-readable strings.
 */
const transformAppointment = (a) => {
  if (!a) return null;
  const rawUtcTimestamp = a.appointmentDateTime || a.dateTime || a.date;
  const parts = utcToKolkataParts(rawUtcTimestamp);

  // Robust patient name resolution & splitting
  const rawPatientName = (a.patientName && a.patientName !== 'Unknown')
    ? a.patientName
    : (a.patient?.name || `${a.patient?.firstName || ''} ${a.patient?.lastName || ''}`.trim() || '');
  const patientParts = (rawPatientName || '').split(' ').filter(Boolean);
  const patientFirstName = a.patient?.firstName || a.patientFirstName || patientParts[0] || (rawPatientName || 'Patient');
  const patientLastName = a.patient?.lastName || a.patientLastName || (patientParts.length > 1 ? patientParts.slice(1).join(' ') : '');
  const patientFullName = rawPatientName || `${patientFirstName} ${patientLastName}`.trim();

  // Robust doctor name resolution & splitting
  const rawDoctorName = (a.doctorName && a.doctorName !== 'Unknown')
    ? a.doctorName
    : (a.doctor?.name || `${a.doctor?.firstName || ''} ${a.doctor?.lastName || ''}`.trim() || '');
  const cleanDoctorName = (rawDoctorName || '').replace(/^Dr\.\s*/i, '').trim();
  const doctorParts = cleanDoctorName.split(' ').filter(Boolean);
  const doctorFirstName = a.doctor?.firstName || a.doctorFirstName || doctorParts[0] || (cleanDoctorName || 'Doctor');
  const doctorLastName = a.doctor?.lastName || a.doctorLastName || (doctorParts.length > 1 ? doctorParts.slice(1).join(' ') : '');
  const formattedDoctorName = rawDoctorName
    ? (rawDoctorName.startsWith('Dr.') ? rawDoctorName : `Dr. ${rawDoctorName}`)
    : (doctorFirstName ? `Dr. ${doctorFirstName} ${doctorLastName}`.trim() : 'Dr. Consultant');

  const aptType = a.type || a.appointmentType || a.reason || 'General Consultation';

  return {
    ...a,
    type: aptType,
    date: parts ? parts.dateString : (a.date || ''),
    time: parts ? parts.time24String : (a.time || ''),
    timeFormatted: parts ? parts.time12String : (a.time || ''),
    formattedDate: parts ? formatDate(rawUtcTimestamp) : (a.date || ''),
    formattedDateTime: parts ? formatDateTime(rawUtcTimestamp) : `${a.date || ''} ${a.time || ''}`,
    patientName: patientFullName,
    doctorName: formattedDoctorName,
    patient: {
      id: a.patientId || a.patient?.id,
      phone: a.patientPhone || a.patient?.phone || a.phone || '',
      patientCode: a.patientCode || a.patient?.patientCode || '',
      gender: a.patientGender || a.patient?.gender || a.gender || '',
      age: a.patientAge || a.patient?.age || a.age,
      ...(a.patient || {}),
      name: patientFullName,
      firstName: patientFirstName,
      lastName: patientLastName,
    },
    doctor: {
      id: a.doctorId || a.doctor?.id,
      specialization: a.doctorSpecialization || a.doctor?.specialization || a.specialization || 'General Medicine',
      ...(a.doctor || {}),
      name: formattedDoctorName,
      firstName: doctorFirstName,
      lastName: doctorLastName,
    },
    clinic: a.clinic || {
      id: a.clinicId,
      name: a.clinicName || 'Main OPD Clinic',
    },
  };
};

export const appointmentService = {
  /**
   * GET /pms/hospital/{hospitalId}/clinic/{clinicId}/appointments
   */
  getAll: async (params = {}, hospitalId) => {
    const activeHospId = getActiveHospitalId(hospitalId);
    const clinicId = getActiveClinicId(params.clinicId);

    let endpoint = `/hospital/${activeHospId}/clinic/${clinicId}/appointments`;
    let queryParams = {
      page: (params.page || 1) - 1,
      size: params.limit || 20,
      status: params.status && params.status !== 'all' ? params.status.toUpperCase() : undefined,
    };

    if (params.doctorId && params.doctorId !== 'all') {
      endpoint = `/hospital/${activeHospId}/clinic/${clinicId}/appointments/doctor/${params.doctorId}`;
    } else if (params.patientId) {
      endpoint = `/hospital/${activeHospId}/clinic/${clinicId}/appointments/patient/${params.patientId}`;
    } else if (params.startDate && params.endDate) {
      endpoint = `/hospital/${activeHospId}/clinic/${clinicId}/appointments/by-date-range`;
      queryParams.startDate = getKolkataStartOfDay(params.startDate);
      queryParams.endDate = getKolkataEndOfDay(params.endDate);
    } else if (params.date) {
      endpoint = `/hospital/${activeHospId}/clinic/${clinicId}/appointments/by-date`;
      queryParams.date = getKolkataStartOfDay(params.date);
    }

    const res = await apiClient.get(endpoint, { params: queryParams });
    const items = (res.content || res.data || []).map(transformAppointment);

    return {
      data: items,
      total: res.totalElements !== undefined ? res.totalElements : items.length,
      page: (res.pageNumber !== undefined ? res.pageNumber : 0) + 1,
      totalPages: res.totalPages || 1,
    };
  },

  /**
   * GET /pms/hospital/{hospitalId}/clinic/{clinicId}/appointments/{appointmentId}
   */
  getById: async (id, clinicId, hospitalId) => {
    const activeHospId = getActiveHospitalId(hospitalId);
    const activeClinicId = getActiveClinicId(clinicId);
    const res = await apiClient.get(`/hospital/${activeHospId}/clinic/${activeClinicId}/appointments/${id}`);
    return transformAppointment(res.data || res);
  },

  /**
   * POST /pms/hospital/{hospitalId}/clinic/{clinicId}/appointments
   */
  create: async (data, hospitalId) => {
    const activeHospId = getActiveHospitalId(hospitalId);
    const clinicId = getActiveClinicId(data.clinicId);
    const apptDateTimeUtc = kolkataToUtcIso(data.date, data.time || '09:00');

    const payload = {
      patientId: data.patientId,
      doctorId: data.doctorId,
      appointmentDateTime: apptDateTimeUtc,
      reason: data.type || data.reason || data.notes,
      notes: data.notes,
    };

    const res = await apiClient.post(`/hospital/${activeHospId}/clinic/${clinicId}/appointments`, payload);
    return transformAppointment(res.data || res);
  },

  /**
   * PUT /pms/hospital/{hospitalId}/clinic/{clinicId}/appointments/{appointmentId}
   */
  update: async (id, data, clinicId, hospitalId) => {
    const activeHospId = getActiveHospitalId(hospitalId);
    const activeClinicId = getActiveClinicId(clinicId);
    const apptDateTimeUtc = data.date && data.time ? kolkataToUtcIso(data.date, data.time) : undefined;
    const payload = {
      patientId: data.patientId,
      doctorId: data.doctorId,
      appointmentDateTime: apptDateTimeUtc,
      reason: data.type || data.reason || data.notes,
      notes: data.notes,
    };

    const res = await apiClient.put(`/hospital/${activeHospId}/clinic/${activeClinicId}/appointments/${id}`, payload);
    return transformAppointment(res.data || res);
  },

  /**
   * PATCH /pms/hospital/{hospitalId}/clinic/{clinicId}/appointments/{appointmentId}/status
   */
  updateStatus: async (id, status, notes = '', clinicId, hospitalId) => {
    const activeHospId = getActiveHospitalId(hospitalId);
    const activeClinicId = getActiveClinicId(clinicId);
    const res = await apiClient.patch(`/hospital/${activeHospId}/clinic/${activeClinicId}/appointments/${id}/status`, {
      status: status.toUpperCase(),
      notes,
    });
    return transformAppointment(res.data || res);
  },

  /**
   * POST /pms/hospital/{hospitalId}/clinic/{clinicId}/appointments/{appointmentId}/reschedule
   */
  reschedule: async (id, newDate, newTime, reason = '', clinicId, hospitalId) => {
    const activeHospId = getActiveHospitalId(hospitalId);
    const activeClinicId = getActiveClinicId(clinicId);
    const newDateTimeUtc = kolkataToUtcIso(newDate, newTime);

    const res = await apiClient.post(`/hospital/${activeHospId}/clinic/${activeClinicId}/appointments/${id}/reschedule`, {
      newAppointmentDateTime: newDateTimeUtc,
      reason,
    });
    return transformAppointment(res.data || res);
  },

  /**
   * Helper to cancel
   */
  cancel: async (id, reason = '', clinicId, hospitalId) => {
    return appointmentService.updateStatus(id, 'CANCELLED', reason, clinicId, hospitalId);
  },

  /**
   * GET /pms/hospital/{hospitalId}/clinic/{clinicId}/appointments/triage
   */
  getTriageQueue: async (clinicId, hospitalId) => {
    const activeHospId = getActiveHospitalId(hospitalId);
    const activeClinicId = getActiveClinicId(clinicId);
    const res = await apiClient.get(`/hospital/${activeHospId}/clinic/${activeClinicId}/appointments/triage`);
    const list = res.data || res || [];
    return Array.isArray(list) ? list.map(transformAppointment) : list;
  },
};

export default appointmentService;
